/**
 * KanbanContextMenu.tsx - Context menu for cards and columns
 * 
 * CHANGE HISTORY:
 * - 251108_0842 [current] danielbeer: feat: Create context menu component
 */

import React, { useEffect, useRef } from 'react';

export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: string;
  action: () => void;
  disabled?: boolean;
  danger?: boolean;
  separator?: boolean;
}

interface KanbanContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

export const KanbanContextMenu: React.FC<KanbanContextMenuProps> = ({
  x,
  y,
  items,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Adjust position if menu would go off-screen
  useEffect(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      if (rect.right > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10;
      }

      if (rect.bottom > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10;
      }

      menuRef.current.style.left = `${adjustedX}px`;
      menuRef.current.style.top = `${adjustedY}px`;
    }
  }, [x, y]);

  return (
    <div
      ref={menuRef}
      className="kanban-context-menu"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        backgroundColor: 'var(--bg-primary, #ffffff)',
        border: '1px solid var(--border-color, #e5e7eb)',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minWidth: '180px',
        padding: '0.25rem',
        zIndex: 1000,
      }}
    >
      {items.map((item, index) => {
        if (item.separator) {
          return (
            <div
              key={`separator-${index}`}
              style={{
                height: '1px',
                backgroundColor: 'var(--border-color, #e5e7eb)',
                margin: '0.25rem 0',
              }}
            />
          );
        }

        return (
          <button
            key={item.id}
            onClick={() => {
              if (!item.disabled) {
                item.action();
                onClose();
              }
            }}
            disabled={item.disabled}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: 'none',
              backgroundColor: 'transparent',
              textAlign: 'left',
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
              color: item.danger
                ? 'var(--color-danger, #ef4444)'
                : item.disabled
                ? 'var(--text-disabled, #9ca3af)'
                : 'var(--text-primary, #1f2937)',
              opacity: item.disabled ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!item.disabled) {
                e.currentTarget.style.backgroundColor = item.danger
                  ? 'var(--bg-danger-hover, #fee2e2)'
                  : 'var(--bg-hover, #f3f4f6)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {item.icon && <span style={{ fontSize: '1rem' }}>{item.icon}</span>}
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * Hook to manage context menu state
 */
export function useContextMenu() {
  const [contextMenu, setContextMenu] = React.useState<{
    x: number;
    y: number;
    items: ContextMenuItem[];
  } | null>(null);

  const showContextMenu = (
    event: React.MouseEvent,
    items: ContextMenuItem[]
  ) => {
    event.preventDefault();
    event.stopPropagation();
    
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      items,
    });
  };

  const hideContextMenu = () => {
    setContextMenu(null);
  };

  return {
    contextMenu,
    showContextMenu,
    hideContextMenu,
  };
}

/**
 * Default card context menu items
 */
export function getCardContextMenuItems(
  cardId: string,
  onEdit: () => void,
  onDuplicate: () => void,
  onDelete: () => void,
  onArchive?: () => void,
  onAddRelationship?: () => void,
  isSelected?: boolean,
  onSelect?: () => void
): ContextMenuItem[] {
  const items: ContextMenuItem[] = [
    {
      id: 'edit',
      label: 'Edit',
      icon: '✏️',
      action: onEdit,
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: '📋',
      action: onDuplicate,
    },
  ];

  if (onSelect) {
    items.push({
      id: 'select',
      label: isSelected ? 'Deselect' : 'Select',
      icon: isSelected ? '☑️' : '☐',
      action: onSelect,
    });
  }

  if (onAddRelationship) {
    items.push({
      id: 'add-relationship',
      label: 'Add Relationship',
      icon: '🔗',
      action: onAddRelationship,
    });
  }

  items.push({ id: 'separator-1', label: '', separator: true, action: () => {} });

  if (onArchive) {
    items.push({
      id: 'archive',
      label: 'Archive',
      icon: '📦',
      action: onArchive,
    });
  }

  items.push({
    id: 'delete',
    label: 'Delete',
    icon: '🗑️',
    action: onDelete,
    danger: true,
  });

  return items;
}

/**
 * Default column context menu items
 */
export function getColumnContextMenuItems(
  columnId: string,
  onEdit: () => void,
  onAddCard: () => void,
  onClearDone?: () => void,
  onSetWipLimit?: () => void,
  onDelete?: () => void
): ContextMenuItem[] {
  const items: ContextMenuItem[] = [
    {
      id: 'add-card',
      label: 'Add Card',
      icon: '➕',
      action: onAddCard,
    },
    {
      id: 'edit',
      label: 'Edit Column',
      icon: '✏️',
      action: onEdit,
    },
  ];

  if (onSetWipLimit) {
    items.push({
      id: 'set-wip-limit',
      label: 'Set WIP Limit',
      icon: '🔢',
      action: onSetWipLimit,
    });
  }

  if (onClearDone) {
    items.push({
      id: 'separator-1',
      label: '',
      separator: true,
      action: () => {},
    });
    items.push({
      id: 'clear-done',
      label: 'Clear Done Cards',
      icon: '🧹',
      action: onClearDone,
    });
  }

  if (onDelete) {
    items.push({
      id: 'separator-2',
      label: '',
      separator: true,
      action: () => {},
    });
    items.push({
      id: 'delete',
      label: 'Delete Column',
      icon: '🗑️',
      action: onDelete,
      danger: true,
    });
  }

  return items;
}
